/**
 * amCharts 4 has no timezone-aware date maths: a `DateAxis` groups, positions and labels by the device timezone, and
 * its own `timezone` setting only rewrites *series* data items — axis ranges (phase lines, data-analysis intervals)
 * keep their raw timestamps and so end up in a different coordinate space to the data they annotate.
 *
 * These helpers are the one place where chart space is entered instead: every date the axis sees is converted here,
 * the axis is put in UTC mode (`dateFormatter.utc`), and amCharts does no shifting of its own. The converted value
 * is kept in a companion property rather than replacing the original, so export, tooltips built from `dataContext`
 * and analysis-line maths keep working on real instants.
 */

/** Suffix of the axis-space copy of a date property */
export const AXIS_DATE_SUFFIX = '__axisSpace';

/**
 * @param {string} dataProperty
 * @return {string}
 */
export const axisDateField = (dataProperty) => `${dataProperty}${AXIS_DATE_SUFFIX}`;

/**
 * @param {Object[]} data
 * @param {string[]} dateProperties - properties plotted on a date axis
 * @param {function(Timestamp|Date|string): Date} toAxisSpace
 * @return {Object[]} copies of `data` — the rows belong to the caller, and `formatChartData` writes to them
 */
export function axisSpaceData(data, dateProperties, toAxisSpace) {
  return data.map((dataItem) => {
    const axisDates = {};

    dateProperties.forEach((dataProperty) => {
      const value = dataItem[dataProperty];
      if (value === null || value === undefined || value === '') {
        return;
      }

      // Not every row has a date for every axis — an unmastered target has no mastery date. Adding no axis
      // property leaves the row for amCharts to drop from the axis, which is what it did with the raw value
      const axisDate = toAxisSpace(value);
      if (!Number.isNaN(axisDate.getTime())) {
        axisDates[axisDateField(dataProperty)] = axisDate;
      }
    });

    return { ...dataItem, ...axisDates };
  });
}

/**
 * A synthetic point for a series plotted on the date axis, carrying both spaces: `date` for the tooltip adapters
 * that compare it against raw analysis-line boundaries, the axis field for plotting.
 *
 * @param {Date} date
 * @param {Object} properties
 * @param {function(Date): Date} toAxisSpace
 * @return {Object}
 */
export function axisSpacePoint(date, properties, toAxisSpace) {
  return { date, [axisDateField('date')]: toAxisSpace(date), ...properties };
}
